import { CandidateProfileTable } from 'modules/candidate-profile/tables/CandidateProfileTable';
import { scrollHorizontallyByKey } from 'common/table/utils/scrollHorizontallyByKey';
import { TableV3Ref } from 'common/table/components/TableV3';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchVectorConversation } from 'modules/candidate-profile/hooks/useSearchVectorConversation';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import { Box, Stack, SxProps, Theme, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { usePathsV2 } from 'core/services/navigation/usePathsV2';
import { CandidateListTab } from 'modules/candidate-profile/stores/useCandidateListStore';
import {
  layoutContainerPaddingX,
  layoutContainerPaddingY
} from 'services/navigation/components/PrivateRoute';
import { CandidateProfileTableHeader } from 'modules/candidate-profile/components/CandidateProfileTableHeader';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle
} from 'react-resizable-panels';
import { candidateProfileTableSx } from 'modules/candidate-profile/styles/candidateProfileTableSx';
import { CandidateProfileSearchConversation } from 'modules/candidate-profile/components/CandidateProfileSearchConversation';
import { SearchCandidateProfileQueryHistoryList } from 'modules/candidate-profile/components/SearchCandidateProfileQueryHistoryList';
import { SearchCandidateProfileQueryHistoryPreview } from 'modules/candidate-profile/components/SearchCandidateProfileQueryHistoryPreview';
import { HandleSelectSearchCandidateProfileQueryProps } from 'modules/candidate-profile/hooks/useSelectSearchCandidateProfileQuery';
import { shouldShowConversation } from 'modules/candidate-profile/utils/shouldShowConversation';
import { CandidateProfileLinkData } from 'modules/candidate-profile/utils/parseMessageWithCandidatesAndChips';
import { useOrganizationCustomFieldConfigs } from 'modules/candidate-profile/hooks/useOrganizationCustomFieldConfigs';
import { useEmbeddedSearchPrefill } from 'modules/atsEmbed/hooks/useEmbeddedSearchPrefill';
import { buildAvailableFilters } from 'modules/candidate-profile/tables/filters/buildSourcingSearchRequest';
import { useCandidateFilterConditions } from 'modules/candidate-profile/hooks/useCandidateFilterConditions';
import { CandidateFilterModeHeader } from 'modules/candidate-profile/components/CandidateFilterModeHeader';
import { CandidateFilterChipRail } from 'modules/candidate-profile/components/CandidateFilterChipRail';
import { describeRangeConditionForChip } from 'modules/candidate-profile/tables/filters/describeRangeConditionForChip';
import { searchColumnForField } from 'modules/candidate-profile/tables/filters/searchesToConditions';
import { useHasSearchCandidateProfileQueryHistory } from 'modules/candidate-profile/hooks/useHasSearchCandidateProfileQueryHistory';
import {
  CandidateFilterNode,
  CandidateFilterState,
  CandidateRangeFilter
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { getManualFilterConditions } from 'modules/candidate-profile/tables/filters/getManualFilterConditions';
import { SearchCandidateProfileMessageSender } from 'generated-graphql/types';
import { withoutTalentPoolFilter } from 'modules/candidate-profile/tables/filters/getTalentPoolCondition';

const mainContainerStack: SxProps<Theme> = {
  px: layoutContainerPaddingX,
  py: layoutContainerPaddingY,
  pb: 'calc(var(--popp-action-bar-height, 0px) + 20px)'
};

export const CandidateProfileList: React.FC<CandidateProfileListProps> = (
  props
) => {
  const tableRef = useRef<TableV3Ref<CandidateProfileTableColumns>>(null);
  const clearSelectionRef = useRef<(() => void) | null>(null);
  const { customFieldConfigs } = useOrganizationCustomFieldConfigs();
  const { prefilledSearchQuery, onPrefillHandled } = useEmbeddedSearchPrefill(
    props.type === CandidateProfileListType.CANDIDATES_LIST
  );
  const availableFilters = useMemo(
    () => buildAvailableFilters(customFieldConfigs),
    [customFieldConfigs]
  );
  const {
    conditions,
    groups,
    joinChipGroups,
    splitChipGroup,
    tableFilters,
    hasFilters,
    handleTableFiltersChanged,
    setMatchMode,
    setModeValues,
    getMatchMode,
    subscribeToModes,
    getValuesForMode,
    removeFilterCondition,
    restoreFromTree,
    mergeAppliedFilters,
    removeFieldFilters,
    clearDismissed,
    clearAll
  } = useCandidateFilterConditions({ availableFilters, tableRef });

  const isSearchColumnField = useCallback(
    (field: string) => Boolean(searchColumnForField(field, availableFilters)),
    [availableFilters]
  );

  const labelForField = useCallback(
    (field: string) =>
      availableFilters.find((available) => available.field === field)?.label ??
      field,
    [availableFilters]
  );

  const describeRangeForChip = (filter: CandidateRangeFilter) =>
    describeRangeConditionForChip(
      filter,
      availableFilters.find((available) => available.field === filter.field)
        ?.range
    );

  const chipRailProps = {
    filters: conditions,
    groups,
    hasFilters,
    labelFor: labelForField,
    isSearchColumn: isSearchColumnField,
    describeRangeForChip,
    onRemove: removeFilterCondition,
    onRemoveField: removeFieldFilters,
    onJoinChipGroups: joinChipGroups,
    onSplitChipGroup: splitChipGroup,
    onClearAll: clearAll,
    onResetTableLayout: () => tableRef.current?.resetTable()
  };

  const filterModeOptions = useMemo(
    () => ({
      availableFilters,
      renderHeader: (field: string) => (
        <CandidateFilterModeHeader
          field={field}
          getMode={getMatchMode}
          subscribeToModes={subscribeToModes}
          getValuesForMode={getValuesForMode}
          onChange={setMatchMode}
          onSetModeValues={setModeValues}
        />
      )
    }),
    [
      availableFilters,
      getMatchMode,
      subscribeToModes,
      getValuesForMode,
      setMatchMode,
      setModeValues
    ]
  );

  const conversationPanelRef = useRef<ImperativePanelHandle>(null);
  const [isConversationPanelCollapsed, setIsConversationPanelCollapsed] =
    useState(false);
  const [
    showSearchCandidateProfileQueryHistoryList,
    setShowSearchCandidateProfileQueryHistoryList
  ] = useState(false);

  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { navigateToPath } = usePathsV2();
  const sessionId = searchParams.get('sessionId');

  // A saved search's filters belong to that search, so what the recruiter
  // dismissed in the conversation they left goes. The page's talent pool goes
  // on every request, so it is not a chip.
  // A restore also counts as a search, so its chips are not manual.
  const [restoreCount, setRestoreCount] = useState(0);
  const restoreSavedFilters = (tree: CandidateFilterNode) => {
    restoreFromTree(withoutTalentPoolFilter(tree, props));
    clearDismissed();
    setRestoreCount((count) => count + 1);
  };

  const {
    candidateProfiles,
    explanationContext,
    isReplying,
    conversationCandidates,
    hasSearchResult,
    loading,
    placeholderMessage,
    conversationHistory,
    isInitialized: isVectorSearchInitialized,
    isResetDisabled,
    searchQueryId,
    handleSearch,
    handleResetConversation,
    handleRegenerateFromMessage,
    refetchVectorSearch
  } = useSearchVectorConversation({
    candidateProfileListProps: props,
    filters: tableFilters,
    filterConditions: conditions,
    filterGroups: groups,
    mergeAppliedFilters: mergeAppliedFilters,
    restoreSavedFilters
  });

  // The chips as the latest search ran them, kept until the next search or
  // restore. The search merges the agent's chips before it adds the user
  // message, so a value added since is the recruiter's.
  const latestSearchId = [...conversationHistory]
    .reverse()
    .find(
      (message) => message.sender === SearchCandidateProfileMessageSender.User
    )?.id;
  const searchedFiltersKey = `${restoreCount}:${latestSearchId}`;
  const [searchedFilters, setSearchedFilters] = useState<{
    key?: string;
    conditions: CandidateFilterState[];
  }>({ conditions: [] });
  if (searchedFilters.key !== searchedFiltersKey) {
    setSearchedFilters({ key: searchedFiltersKey, conditions });
  }
  const manualFilters = getManualFilterConditions(
    conditions,
    searchedFilters.conditions
  );

  const { hasHistory, excludeDeleted } =
    useHasSearchCandidateProfileQueryHistory({
      conversationHistoryLength: conversationHistory.length
    });

  const hasConversation =
    conversationHistory.length > 0 || !!placeholderMessage;

  const isConversationSettled = !searchQueryId || isVectorSearchInitialized;

  // Merge vector search candidate profiles (which have full_name from the vector DB)
  // with conversation cache candidates, so the conversation panel can resolve names
  // from either source. Vector search data takes priority as it has the most up-to-date names.
  const allCandidateProfileLinks = useMemo<CandidateProfileLinkData[]>(() => {
    const vectorSearchLinks: CandidateProfileLinkData[] = candidateProfiles.map(
      (cp) => ({
        id: cp.candidate_profile_id,
        full_name: cp.full_name
      })
    );

    const merged = [...vectorSearchLinks];
    conversationCandidates.forEach((cc) => {
      if (!merged.some((m) => m.id === cc.id)) {
        merged.push(cc);
      }
    });
    return merged;
  }, [candidateProfiles, conversationCandidates]);

  const handleToggleConversationPanel = () => {
    const panel = conversationPanelRef.current;
    if (!panel) return;

    if (isConversationPanelCollapsed) {
      panel.expand();
    } else {
      panel.collapse();
    }
  };

  // A filter the recruiter took off stays off for the whole conversation, a
  // follow-up question included. Only a reset or a reopened search clears it.
  const sendFromConversation = useCallback(
    (message: string, removeFields?: string[]) => {
      clearSelectionRef.current?.();
      handleSearch(message, removeFields);
    },
    [handleSearch]
  );

  const resetConversationAndFilters = () => {
    handleResetConversation();
    // A geo radius or a range has no column to sit in, so clearing the table
    // leaves it standing. clearAll drops the conditions themselves.
    clearAll();
  };

  const expandConversationPanel = () => {
    if (isConversationPanelCollapsed) {
      conversationPanelRef.current?.expand();
    }
  };

  const handleSearchHistoryClick = () => {
    expandConversationPanel();
    setShowSearchCandidateProfileQueryHistoryList(true);
  };

  const handleNewSearchClick = () => {
    expandConversationPanel();
    setShowSearchCandidateProfileQueryHistoryList(false);
    resetConversationAndFilters();
  };

  // Deleting the open search leaves nothing to show, so start a new one.
  const [deletedOpenSearchId, setDeletedOpenSearchId] = useState<string | null>(
    null
  );

  const handleDeleteSearchCandidateProfileQuery = (id: string) => {
    excludeDeleted(id);
    if (id === searchQueryId) {
      setDeletedOpenSearchId(id);
    }
  };

  // A search in flight blocks the reset, so wait for it to end. Skip the
  // reset if the recruiter opened another search in the meantime.
  useEffect(() => {
    if (!deletedOpenSearchId || isResetDisabled) {
      return;
    }
    setDeletedOpenSearchId(null);
    if (deletedOpenSearchId === searchQueryId) {
      resetConversationAndFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedOpenSearchId, isResetDisabled, searchQueryId]);

  const handleSelectSearchCandidateProfileQuery = (
    data: HandleSelectSearchCandidateProfileQueryProps
  ) => {
    // A reply in flight lands on whichever search is open, so none opens until
    // it does. An auto-sent brief can start one with the history already open.
    if (isResetDisabled) return;

    const newSearchParams: Record<string, string> = {
      searchQueryId: data.searchCandidateProfileQueryId,
      tab: CandidateListTab.CANDIDATES
    };

    if (sessionId) {
      newSearchParams['sessionId'] = sessionId;
    }

    if (
      props.type === CandidateProfileListType.ANALYSIS_CANDIDATES_LIST ||
      props.type ===
        CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL
    ) {
      navigateToPath.ANALYSE_CANDIDATE_PROFILE_LIST(
        {
          candidateBatchId: props.candidateBatchId,
          searchParams: newSearchParams
        },
        { replace: true }
      );
    } else {
      navigateToPath.CANDIDATE_PROFILE_LIST(
        { searchParams: newSearchParams },
        { replace: true }
      );
    }

    // A stored tree records what the search actually applied, including the
    // modes, ranges and geo radius the flat columns cannot hold, so it wins.
    // Searches predating the sourcing endpoint have none: fall back to the
    // columns and restore what they can express.
    if (data.filterTree) {
      restoreSavedFilters(data.filterTree);
    } else {
      // A search with no filters lands here too. Clear first: a geo radius has
      // no column, so applying the columns alone leaves it on.
      clearAll();
      // TableV3 will sync these to the URL.
      tableRef.current?.applyFilters(data.filters);
    }

    setShowSearchCandidateProfileQueryHistoryList(false);
  };

  // ag-grid's viewport owns horizontal scrolling and takes no focus of its own.
  const handleScrollRegionKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) =>
    scrollHorizontallyByKey(
      event,
      event.currentTarget.querySelector<HTMLElement>(
        '.ag-body-horizontal-scroll-viewport'
      )
    );

  const shouldShowConversationValue = shouldShowConversation(props);

  if (shouldShowConversationValue) {
    return (
      <Box sx={candidateProfileTableSx.container}>
        <PanelGroup
          direction="horizontal"
          autoSaveId="candidate-profile-table-layout"
          style={candidateProfileTableSx.panelGroupStyle}
        >
          <Panel
            defaultSize={67}
            minSize={50}
            style={
              {
                ...candidateProfileTableSx.leftPanelStyle,
                '--popp-color-primary': theme.palette.primary.main
              } as React.CSSProperties
            }
            data-scroll-container="candidate-profile-table"
            tabIndex={0}
            role="region"
            aria-label="Candidate search results"
            onKeyDown={handleScrollRegionKeyDown}
          >
            <Stack
              sx={candidateProfileTableSx.mainContainerStack(
                layoutContainerPaddingX,
                layoutContainerPaddingY
              )}
            >
              <CandidateProfileTableHeader
                candidateProfileListProps={props}
                conversationPanelOptions={{
                  isConversationPanelCollapsed,
                  handleToggleConversationPanel
                }}
              />
              <CandidateFilterChipRail
                {...chipRailProps}
                onSearchHistory={
                  hasHistory ? handleSearchHistoryClick : undefined
                }
                onNewSearch={
                  hasConversation || !isConversationSettled
                    ? handleNewSearchClick
                    : undefined
                }
                isReplying={isResetDisabled}
                isAnchored
              />
              <CandidateProfileTable
                candidateProfileListProps={props}
                tableRef={tableRef}
                clearSelectionRef={clearSelectionRef}
                filters={tableFilters}
                filterConditions={conditions}
                filterGroups={groups}
                hasSearchResult={hasSearchResult}
                candidateProfiles={candidateProfiles}
                explanationContext={explanationContext}
                isVectorSearchInitialized={isVectorSearchInitialized}
                isSearchLoading={loading}
                conversationProps={{
                  isConversationLoading: loading,
                  placeholderMessage
                }}
                onChangeFilters={handleTableFiltersChanged}
                filterModeOptions={filterModeOptions}
                onRefetchVectorSearch={refetchVectorSearch}
              />
            </Stack>
          </Panel>
          <PanelResizeHandle
            style={candidateProfileTableSx.getPanelResizeHandleStyle(
              theme.palette.primary.main
            )}
          >
            <Box sx={candidateProfileTableSx.resizeHandle} />
          </PanelResizeHandle>
          <Panel
            ref={conversationPanelRef}
            defaultSize={33}
            minSize={15}
            maxSize={40}
            collapsible
            onCollapse={() => setIsConversationPanelCollapsed(true)}
            onExpand={() => setIsConversationPanelCollapsed(false)}
            style={candidateProfileTableSx.conversationPanelStyle}
          >
            {!showSearchCandidateProfileQueryHistoryList && (
              <CandidateProfileSearchConversation
                conversationHistory={conversationHistory}
                placeholderMessage={placeholderMessage}
                candidateProfiles={allCandidateProfileLinks}
                isQueryLoading={loading}
                isReplying={isReplying}
                manualFilters={manualFilters}
                labelForField={labelForField}
                isSearchColumn={isSearchColumnField}
                describeRangeForChip={describeRangeForChip}
                onSearch={sendFromConversation}
                onRegenerateFromMessage={handleRegenerateFromMessage}
                onClose={() => conversationPanelRef.current?.collapse()}
                isConversationInitialized={isConversationSettled}
                prefilledSearchQuery={prefilledSearchQuery}
                onPrefillHandled={onPrefillHandled}
                historyPreview={
                  hasHistory ? (
                    <SearchCandidateProfileQueryHistoryPreview
                      onSelectSearchCandidateProfileQuery={
                        handleSelectSearchCandidateProfileQuery
                      }
                      onViewAll={handleSearchHistoryClick}
                      onDeleteSearchCandidateProfileQuery={
                        handleDeleteSearchCandidateProfileQuery
                      }
                    />
                  ) : null
                }
              />
            )}
            {showSearchCandidateProfileQueryHistoryList && (
              <SearchCandidateProfileQueryHistoryList
                onSelectSearchCandidateProfileQuery={
                  handleSelectSearchCandidateProfileQuery
                }
                onClose={() =>
                  setShowSearchCandidateProfileQueryHistoryList(false)
                }
                onDeleteSearchCandidateProfileQuery={
                  handleDeleteSearchCandidateProfileQuery
                }
              />
            )}
          </Panel>
        </PanelGroup>
      </Box>
    );
  }

  return (
    <Box sx={mainContainerStack}>
      <CandidateProfileTableHeader
        candidateProfileListProps={props}
        conversationPanelOptions={null}
      />
      <CandidateFilterChipRail {...chipRailProps} />
      <CandidateProfileTable
        candidateProfileListProps={props}
        tableRef={tableRef}
        clearSelectionRef={clearSelectionRef}
        filters={tableFilters}
        filterConditions={conditions}
        filterGroups={groups}
        hasSearchResult={hasSearchResult}
        candidateProfiles={candidateProfiles}
        explanationContext={explanationContext}
        isVectorSearchInitialized={isVectorSearchInitialized}
        isSearchLoading={loading}
        conversationProps={null}
        onChangeFilters={handleTableFiltersChanged}
        filterModeOptions={filterModeOptions}
        onRefetchVectorSearch={refetchVectorSearch}
      />
    </Box>
  );
};
